import React, { useState } from 'react';
import { Video, Users, Shield, Clock, CheckCircle2, ArrowRight } from 'lucide-react';
import { JitsiMeetings } from '../components/JitsiMeetings';
import { Footer } from '../components/Footer';

interface MeetingsProps {
  showMeeting?: boolean;
  setShowMeeting?: (show: boolean) => void;
}

export function Meetings({ showMeeting: propShowMeeting, setShowMeeting: propSetShowMeeting }: MeetingsProps) {
  const [localShowMeeting, setLocalShowMeeting] = useState(false);
  
  // Use either props or local state
  const showMeeting = typeof propShowMeeting !== 'undefined' ? propShowMeeting : localShowMeeting;
  const setShowMeeting = propSetShowMeeting || setLocalShowMeeting;

  if (showMeeting) {
    return <JitsiMeetings onClose={() => setShowMeeting(false)} />;
  }

  return (
    <div className="min-h-screen py-24">
      <div className="container mx-auto px-6">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-7xl font-bold mb-8 bg-gradient-to-r from-blue-400 to-purple-500 text-transparent bg-clip-text">
            Meet with Techton's Experts
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto">
            Join our secure video conference to discuss your project needs and discover how we can transform your digital presence.
          </p>
          <button
            onClick={() => setShowMeeting(true)}
            className="bg-blue-500 hover:bg-blue-600 disabled:bg-blue-800 disabled:cursor-wait px-8 py-4 rounded-full font-medium transition inline-flex items-center"
            aria-label="Start video meeting now"
          >
            Join Video Meeting <ArrowRight className="ml-2 w-5 h-5" />
          </button>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-24">
          <div className="bg-slate-800 p-8 rounded-2xl">
            <Video className="w-12 h-12 text-blue-400 mb-6" />
            <h3 className="text-xl font-bold mb-4">HD Video Quality</h3>
            <p className="text-gray-400 mb-6">
              Crystal clear video and audio for seamless communication.
            </p>
            <ul className="space-y-3">
              <li className="flex items-center text-gray-300">
                <CheckCircle2 className="w-5 h-5 text-blue-400 mr-2" />
                <span>1080p HD video</span>
              </li>
              <li className="flex items-center text-gray-300">
                <CheckCircle2 className="w-5 h-5 text-blue-400 mr-2" />
                <span>Echo cancellation</span>
              </li>
              <li className="flex items-center text-gray-300">
                <CheckCircle2 className="w-5 h-5 text-blue-400 mr-2" />
                <span>Noise suppression</span>
              </li>
            </ul>
          </div>

          <div className="bg-slate-800 p-8 rounded-2xl">
            <Shield className="w-12 h-12 text-blue-400 mb-6" />
            <h3 className="text-xl font-bold mb-4">Secure Meetings</h3>
            <p className="text-gray-400 mb-6">
              Enterprise-grade security for confidential discussions.
            </p>
            <ul className="space-y-3">
              <li className="flex items-center text-gray-300">
                <CheckCircle2 className="w-5 h-5 text-blue-400 mr-2" />
                <span>End-to-end encryption</span>
              </li>
              <li className="flex items-center text-gray-300">
                <CheckCircle2 className="w-5 h-5 text-blue-400 mr-2" />
                <span>Secure room access</span>
              </li>
              <li className="flex items-center text-gray-300">
                <CheckCircle2 className="w-5 h-5 text-blue-400 mr-2" />
                <span>Private chat</span>
              </li>
            </ul>
          </div>

          <div className="bg-slate-800 p-8 rounded-2xl">
            <Users className="w-12 h-12 text-blue-400 mb-6" />
            <h3 className="text-xl font-bold mb-4">Collaboration Tools</h3>
            <p className="text-gray-400 mb-6">
              Interactive features for productive meetings.
            </p>
            <ul className="space-y-3">
              <li className="flex items-center text-gray-300">
                <CheckCircle2 className="w-5 h-5 text-blue-400 mr-2" />
                <span>Screen sharing</span>
              </li>
              <li className="flex items-center text-gray-300">
                <CheckCircle2 className="w-5 h-5 text-blue-400 mr-2" />
                <span>File sharing</span>
              </li>
              <li className="flex items-center text-gray-300">
                <CheckCircle2 className="w-5 h-5 text-blue-400 mr-2" />
                <span>Meeting chat</span>
              </li>
            </ul>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-12 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Transform Your Digital Presence?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Join a meeting with our expert team to discuss your project requirements and discover how we can help.
          </p>
          <button
            onClick={() => setShowMeeting(true)}
            className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-full font-medium transition"
          >
            Start Video Meeting Now
          </button>
        </div>
      </div>
      <Footer />
    </div>
  );
}